import { useApp } from '../contexts/AppContext';
import { useTr } from '../utils/useTr';
import { loadTranslationOverrides } from '../utils/i18n';
import { loadPageContent } from '../utils/cms';

// ── Static page content — editable via Admin › Site text ─────────────────
// Keys prefixed with page id so Admin can edit each field individually.

export const BASE_PAGES = {
  about: {
    icon: '✦',
    title: { en: 'About BeeLove', fr: 'À propos de BeeLove' },
    subtitle: {
      en: 'Making dating more honest, more human, and more bee-utiful.',
      fr: 'Rendre les rencontres plus honnêtes, plus humaines, et plus belle-ment authentiques.',
    },
    sections: [
      {
        heading: { en: 'Our mission', fr: 'Notre mission' },
        body: {
          en: `BeeLove was born from a simple frustration: dating apps show you the best version someone wants to project — not who they actually are. We asked: what if the people who know you best could vouch for you?\n\nWe built a platform where your friends answer a quiz about your personality, character, and how you show up in relationships. That collective portrait becomes the foundation of your compatibility profile — more honest, more nuanced, and more human than any self-written bio.`,
          fr: `BeeLove est né d'une frustration simple : les apps de rencontres te montrent la meilleure version que quelqu'un veut projeter — pas qui il est vraiment. Nous nous sommes posé la question : et si les gens qui te connaissent le mieux pouvaient te représenter ?\n\nNous avons créé une plateforme où tes amis répondent à un quiz sur ta personnalité, ton caractère et ta façon d'être en relation. Ce portrait collectif devient le fondement de ton profil de compatibilité — plus honnête, plus nuancé, et plus humain que n'importe quelle bio auto-écrite.`,
        },
      },
      {
        heading: { en: 'Who we are', fr: 'Qui nous sommes' },
        body: {
          en: `We are a small team based in Montréal, passionate about building technology that brings real people closer together. We believe the best relationships start with authenticity — and that authenticity is best validated by the people who already love you.\n\nBeeLove is for people who are done performing for strangers and ready to be genuinely known.`,
          fr: `Nous sommes une petite équipe basée à Montréal, passionnée par la création de technologies qui rapprochent les vraies personnes. Nous croyons que les meilleures relations commencent par l'authenticité — et que cette authenticité est mieux validée par les personnes qui t'aiment déjà.\n\nBeeLove est fait pour ceux qui en ont assez de jouer la comédie pour des inconnus et qui sont prêts à être vraiment connus.`,
        },
      },
      {
        heading: { en: 'Our values', fr: 'Nos valeurs' },
        body: {
          en: `**Authenticity first.** We never incentivise you to present a filtered version of yourself.\n\n**Privacy by design.** Your data belongs to you. Friend quiz scores feed only your aggregated compatibility profile — individual responses are never shown to you.\n\n**Community over algorithm.** The best matching signal is a human vouching for another human.\n\n**Safety always.** Every profile is reviewed by our team. Every report is taken seriously.`,
          fr: `**L'authenticité d'abord.** Nous ne t'incitez jamais à présenter une version filtrée de toi-même.\n\n**La vie privée par conception.** Tes données t'appartiennent. Les scores du quiz de tes amis alimentent uniquement ton profil de compatibilité agrégé — les réponses individuelles ne te sont jamais montrées.\n\n**La communauté avant l'algorithme.** Le meilleur signal de matching est un humain qui répond pour un autre humain.\n\n**La sécurité toujours.** Chaque profil est examiné par notre équipe. Chaque signalement est traité sérieusement.`,
        },
      },
    ],
  },

  how: {
    icon: '✦',
    title: { en: 'How BeeLove works', fr: 'Comment BeeLove fonctionne' },
    subtitle: {
      en: 'Your friends know you. Their answers reveal your true compatibility.',
      fr: 'Tes amis te connaissent. Leurs réponses révèlent ta vraie compatibilité.',
    },
    sections: [
      {
        heading: { en: 'Step by step', fr: 'Étape par étape' },
        steps: [
          {
            n: '01',
            en: { title:'Create your profile', body:'Photos, bio, a few prompts. Takes 3 minutes. This is just the foundation — the real picture comes from your friends.' },
            fr: { title:'Crée ton profil', body:'Photos, bio, quelques prompts. 3 minutes. Ce n\'est que la base — la vraie image vient de tes amis.' },
          },
          {
            n: '02',
            en: { title:'Invite your friends', body:'Share your unique link with at least 5 friends. They answer 10 questions about your personality, character, and relationship style — anonymously.' },
            fr: { title:'Invite tes amis', body:'Partage ton lien unique avec au moins 5 amis. Ils répondent à 10 questions sur ta personnalité, ton caractère et ton style de relation — anonymement.' },
          },
          {
            n: '03',
            en: { title:'Your compatibility profile unlocks', body:'Once 5 friends respond, your compatibility profile is revealed. It shows how you show up in relationships — based on real people who know you, not your own marketing.' },
            fr: { title:'Ton profil de compatibilité se déverrouille', body:'Dès que 5 amis répondent, ton profil de compatibilité est révélé. Il montre comment tu te comportes en relation — basé sur de vraies personnes qui te connaissent, pas ton propre marketing.' },
          },
          {
            n: '04',
            en: { title:'Match and connect', body:'The algorithm surfaces profiles that are genuinely compatible with yours. Every match is grounded in real compatibility — not just photos.' },
            fr: { title:'Matche et connecte', body:'L\'algorithme fait remonter les profils vraiment compatibles avec le tien. Chaque match est ancré dans une vraie compatibilité — pas seulement des photos.' },
          },
          {
            n: '05',
            en: { title:'Bee Loved', body:'Meet IRL at BeeLove events, connect through the BeeHive community, and build something real.' },
            fr: { title:'Bee Loved', body:'Rencontre IRL lors d\'événements BeeLove, connecte-toi via la communauté La Ruche, et construis quelque chose de vrai.' },
          },
        ],
      },
    ],
  },

  privacy: {
    icon: '✦',
    title: { en: 'Privacy Policy', fr: 'Politique de confidentialité' },
    subtitle: {
      en: 'How we collect, use, and protect your personal data. Last updated April 2026.',
      fr: 'Comment nous collectons, utilisons et protégeons vos données personnelles. Dernière mise à jour : avril 2026.',
    },
    sections: [
      {
        heading: { en: 'Data we collect', fr: 'Données que nous collectons' },
        body: {
          en: `We collect information you provide when creating an account (name, email, photos, bio), information your friends provide through the compatibility quiz (anonymised and aggregated), usage data (pages visited, features used), and device information for security purposes.\n\nWe never collect your precise location without explicit permission. We never access your contacts without permission.`,
          fr: `Nous collectons les informations que vous fournissez lors de la création d'un compte (nom, e-mail, photos, bio), les informations que vos amis fournissent via le quiz de compatibilité (anonymisées et agrégées), les données d'utilisation (pages visitées, fonctionnalités utilisées), et les informations sur l'appareil à des fins de sécurité.\n\nNous ne collectons jamais votre localisation précise sans permission explicite. Nous n'accédons jamais à vos contacts sans permission.`,
        },
      },
      {
        heading: { en: 'How we use your data', fr: 'Comment nous utilisons vos données' },
        body: {
          en: `Your data is used to: provide and improve the BeeLove service, match you with compatible profiles, send notifications you have opted into, and ensure the safety of our community.\n\nWe never sell your personal data to advertisers or third parties. We never use your data to target you with third-party advertising.`,
          fr: `Vos données sont utilisées pour : fournir et améliorer le service BeeLove, vous mettre en relation avec des profils compatibles, envoyer des notifications auxquelles vous avez souscrit, et assurer la sécurité de notre communauté.\n\nNous ne vendons jamais vos données personnelles à des annonceurs ou à des tiers. Nous n'utilisons jamais vos données pour vous cibler avec de la publicité tierce.`,
        },
      },
      {
        heading: { en: 'Friend quiz data', fr: 'Données du quiz amis' },
        body: {
          en: `Your friends' quiz scores are combined into your aggregated compatibility profile. You see only the aggregate — individual scores are not attributed to specific people. Your friends may optionally provide their first name, which may appear alongside any personal quote they share with you.\n\nYour friends do not need a BeeLove account to complete the quiz. We do not store their contact information beyond what is necessary to process their response.`,
          fr: `Les scores du quiz de vos amis sont combinés dans votre profil de compatibilité agrégé. Vous ne voyez que le résultat agrégé — les scores individuels ne sont pas attribués à des personnes spécifiques. Vos amis peuvent optionnellement fournir leur prénom, qui peut apparaître avec toute citation personnelle qu'ils partagent avec vous.\n\nVos amis n'ont pas besoin d'un compte BeeLove pour compléter le quiz. Nous ne stockons pas leurs coordonnées au-delà de ce qui est nécessaire pour traiter leur réponse.`,
        },
      },
      {
        heading: { en: 'Your rights', fr: 'Vos droits' },
        body: {
          en: `You have the right to access, correct, or delete your personal data at any time. To request deletion of your account and all associated data, email privacy@beelove.me. We will process your request within 30 days.\n\nYou may also request a copy of all data we hold about you (data portability) or restrict how we process your data.`,
          fr: `Vous avez le droit d'accéder, de corriger ou de supprimer vos données personnelles à tout moment. Pour demander la suppression de votre compte et de toutes les données associées, envoyez un e-mail à privacy@beelove.me. Nous traiterons votre demande dans les 30 jours.\n\nVous pouvez également demander une copie de toutes les données que nous détenons vous concernant (portabilité des données) ou restreindre la façon dont nous traitons vos données.`,
        },
      },
    ],
  },

  terms: {
    icon: '✦',
    title: { en: 'Terms of Use', fr: 'Conditions d\'utilisation' },
    subtitle: {
      en: 'The rules of the hive. Last updated April 2026.',
      fr: 'Les règles de la ruche. Dernière mise à jour : avril 2026.',
    },
    sections: [
      {
        heading: { en: 'Membership', fr: 'Adhésion' },
        body: {
          en: `You must be 18 years or older to use BeeLove. By creating an account, you confirm that you are legally permitted to enter into a binding agreement and that all information you provide is accurate.\n\nOne account per person. Creating multiple accounts or impersonating another person will result in immediate permanent suspension.`,
          fr: `Vous devez avoir 18 ans ou plus pour utiliser BeeLove. En créant un compte, vous confirmez que vous êtes légalement autorisé à conclure un accord contraignant et que toutes les informations que vous fournissez sont exactes.\n\nUn compte par personne. La création de plusieurs comptes ou l'usurpation d'identité entraînera une suspension permanente immédiate.`,
        },
      },
      {
        heading: { en: 'Behaviour standards', fr: 'Standards de comportement' },
        body: {
          en: `BeeLove is a community built on respect. You agree to: treat all members with dignity, never send harassing or threatening messages, not share explicit content without explicit mutual consent, not use BeeLove for commercial solicitation, and report profiles or content that violates these terms.\n\nViolations may result in immediate account suspension without refund.`,
          fr: `BeeLove est une communauté fondée sur le respect. Vous acceptez de : traiter tous les membres avec dignité, ne jamais envoyer de messages harcelants ou menaçants, ne pas partager de contenu explicite sans consentement mutuel explicite, ne pas utiliser BeeLove pour la sollicitation commerciale, et signaler les profils ou contenus qui enfreignent ces conditions.\n\nLes violations peuvent entraîner une suspension immédiate du compte sans remboursement.`,
        },
      },
      {
        heading: { en: 'Content ownership', fr: 'Propriété du contenu' },
        body: {
          en: `You retain ownership of all content you upload to BeeLove (photos, bio, prompts). By uploading content, you grant BeeLove a non-exclusive, royalty-free licence to display that content within the service.\n\nYou may delete your content at any time from your profile settings. Upon account deletion, your content is removed from our servers within 30 days.`,
          fr: `Vous conservez la propriété de tout le contenu que vous téléchargez sur BeeLove (photos, bio, prompts). En téléchargeant du contenu, vous accordez à BeeLove une licence non exclusive et sans redevance pour afficher ce contenu dans le service.\n\nVous pouvez supprimer votre contenu à tout moment depuis les paramètres de votre profil. Lors de la suppression du compte, votre contenu est supprimé de nos serveurs dans les 30 jours.`,
        },
      },
    ],
  },

  safety: {
    icon: '✦',
    title: { en: 'Safety at BeeLove', fr: 'Sécurité sur BeeLove' },
    subtitle: {
      en: 'Your safety is our highest priority.',
      fr: 'Votre sécurité est notre priorité absolue.',
    },
    sections: [
      {
        heading: { en: 'Profile verification', fr: 'Vérification du profil' },
        body: {
          en: `All profiles are reviewed by our moderation team. Profiles with suspicious activity are flagged and reviewed as a priority.\n\nThe friend quiz system is our strongest safety mechanism: it is impossible to build a high-quality compatibility profile without real people vouching for you. This structurally limits fake profiles.`,
          fr: `Tous les profils sont examinés par notre équipe de modération. Les profils présentant une activité suspecte sont signalés et examinés en priorité.\n\nLe système de quiz amis est notre mécanisme de sécurité le plus puissant : il est impossible de construire un profil de compatibilité de haute qualité sans que de vraies personnes vous cautionnent. Cela limite structurellement les faux profils.`,
        },
      },
      {
        heading: { en: 'Reporting & blocking', fr: 'Signalement et blocage' },
        body: {
          en: `You can report any profile by tapping the ⓘ icon on their profile or in the chat. Reports are reviewed promptly. For urgent safety issues, reports are escalated immediately.\n\nThe block button is always available in every profile and conversation. A blocked user cannot see your profile, contact you, or appear in your Discover feed.`,
          fr: `Vous pouvez signaler n'importe quel profil en appuyant sur l'icône ⓘ sur son profil ou dans la conversation. Les signalements sont examinés rapidement. Pour les problèmes de sécurité urgents, les signalements sont escaladés immédiatement.\n\nLe bouton de blocage est toujours disponible dans chaque profil et conversation. Un utilisateur bloqué ne peut pas voir votre profil, vous contacter, ni apparaître dans votre fil Discover.`,
        },
      },
      {
        heading: { en: 'Safe dating tips', fr: 'Conseils de rencontres sécurisées' },
        body: {
          en: `**First meeting:** Always meet in a public place. Tell a friend where you are going. Trust your instincts — it is always OK to leave.\n\n**Personal information:** Never share your home address, workplace, or financial information with someone you haven't met in person multiple times.\n\n**Photos and content:** Never share intimate photos. Once sent, you cannot control where they go.\n\n**Emergency:** If you feel unsafe at any point, call emergency services (911) immediately.`,
          fr: `**Première rencontre :** Rencontrez-vous toujours dans un lieu public. Dites à un ami où vous allez. Faites confiance à votre instinct — il est toujours acceptable de partir.\n\n**Informations personnelles :** Ne partagez jamais votre adresse personnelle, votre lieu de travail ou vos informations financières avec quelqu'un que vous n'avez pas rencontré en personne plusieurs fois.\n\n**Photos et contenu :** Ne partagez jamais de photos intimes. Une fois envoyées, vous ne pouvez plus contrôler où elles vont.\n\n**Urgence :** Si vous vous sentez en danger à tout moment, appelez immédiatement les services d'urgence (911).`,
        },
      },
    ],
  },

  contact: {
    icon: '✦',
    title: { en: 'Contact Us', fr: 'Contactez-nous' },
    subtitle: {
      en: 'We would love to hear from you. We aim to respond within 48 hours.',
      fr: 'Nous serions ravis de vous entendre. Nous visons à répondre dans les 48 heures.',
    },
    sections: [
      {
        heading: { en: 'Get in touch', fr: 'Prenez contact' },
        contacts: [
          { label: { en: 'General enquiries', fr: 'Demandes générales' }, value: 'hello@beelove.me' },
          { label: { en: 'Support', fr: 'Assistance' }, value: 'support@beelove.me' },
          { label: { en: 'Press & media', fr: 'Presse et médias' }, value: 'press@beelove.me' },
          { label: { en: 'Privacy requests', fr: 'Demandes de confidentialité' }, value: 'privacy@beelove.me' },
          { label: { en: 'Partnerships', fr: 'Partenariats' }, value: 'partners@beelove.me' },
        ],
      },
    ],
  },

  pricing: {
    icon: '✦',
    title: { en: 'Pricing', fr: 'Tarifs' },
    subtitle: {
      en: 'Free for Founding Members. Paid plans arrive when the community is ready.',
      fr: 'Gratuit pour les Founding Members. Les forfaits payants arriveront quand la communauté sera prête.',
    },
    sections: [
      {
        heading: { en: 'Founding Member — free, for life', fr: 'Founding Member — gratuit, à vie' },
        body: {
          en: `During the launch phase, every new member joins as a **Founding Member**. That means:\n\n• **Free, lifetime access to the app** — matches, messages, compatibility quiz built by your friends, the Hive community, your full profile. No limits, no gates, no expiry.\n\n• **Founding Member badge — for life.** Even when we introduce paid plans, your Founding Member status keeps the app on the free tier forever.\n\n• **No credit card required.** You are not on a trial. You are helping us build the community.\n\n**Paid plans (later this year):** Bee Honey and Bee Gold will unlock premium perks for new members who join after the founding phase. Founding Members keep everything they have today.`,
          fr: `Pendant la phase de lancement, chaque nouveau membre rejoint comme **Founding Member**. Ça veut dire :\n\n• **Accès gratuit à vie à l'app** — matchs, messages, quiz de compatibilité rempli par tes amis, communauté Ruche, ton profil complet. Aucune limite, aucune barrière, aucune expiration.\n\n• **Badge Founding Member — à vie.** Même quand nous lancerons les forfaits payants, ton statut Founding Member te garde l'app gratuite pour toujours.\n\n• **Pas de carte bancaire.** Tu n'es pas en essai. Tu aides à construire la communauté.\n\n**Forfaits payants (plus tard cette année) :** Bee Honey et Bee Gold offriront des avantages premium aux nouveaux membres qui rejoignent après la phase fondatrice. Les Founding Members gardent tout ce qu'ils ont aujourd'hui.`,
        },
      },
    ],
  },

  help: {
    icon: '✦',
    title: { en: 'Help Center', fr: 'Centre d\'aide' },
    subtitle: {
      en: 'Answers to the most common questions.',
      fr: 'Réponses aux questions les plus fréquentes.',
    },
    sections: [
      {
        heading: { en: 'Getting started', fr: 'Pour commencer' },
        faqs: [
          {
            q: { en: 'How do I get my friends to fill in the quiz?', fr: 'Comment faire remplir le quiz à mes amis ?' },
            a: { en: 'Go to Profile → Invite tab. You\'ll find your unique rating link there. Share it via WhatsApp, SMS, or email. Your friends don\'t need a BeeLove account — the quiz takes about 5 minutes.', fr: 'Va dans Profil → onglet Inviter. Tu y trouveras ton lien unique. Partage-le via WhatsApp, SMS ou e-mail. Tes amis n\'ont pas besoin d\'un compte BeeLove — le quiz prend environ 5 minutes.' },
          },
          {
            q: { en: 'Can I change my compatibility quiz answers?', fr: 'Puis-je modifier mes réponses au quiz de compatibilité ?' },
            a: { en: 'Yes — go to Profile → Settings → Compatibility Quiz → Update answers. Your compatibility scores with existing matches will update automatically.', fr: 'Oui — va dans Profil → Paramètres → Quiz de compatibilité → Mettre à jour les réponses. Tes scores de compatibilité avec les matchs existants se mettront à jour automatiquement.' },
          },
          {
            q: { en: 'Are friend quiz answers anonymous?', fr: 'Les réponses au quiz des amis sont-elles anonymes ?' },
            a: { en: 'Individual scores are combined into an aggregated compatibility profile — you never see who scored what. If a friend chooses to leave a personal quote with their name, that quote may appear on your profile.', fr: 'Les scores individuels sont combinés dans un profil de compatibilité agrégé — tu ne vois jamais qui a mis quoi. Si un ami choisit de laisser une citation personnelle avec son prénom, cette citation peut apparaître sur ton profil.' },
          },
        ],
      },
      {
        heading: { en: 'Safety & privacy', fr: 'Sécurité et confidentialité' },
        faqs: [
          {
            q: { en: 'How do I report someone?', fr: 'Comment signaler quelqu\'un ?' },
            a: { en: 'Tap the ⓘ icon on any profile or in any chat conversation. Select "Report" and choose the reason. Our team reviews all reports promptly.', fr: 'Appuie sur l\'icône ⓘ sur n\'importe quel profil ou dans n\'importe quelle conversation. Sélectionne "Signaler" et choisis la raison. Notre équipe examine tous les signalements rapidement.' },
          },
          {
            q: { en: 'How do I delete my account?', fr: 'Comment supprimer mon compte ?' },
            a: { en: 'Go to Profile → Settings → Account → Delete account. All your data will be permanently deleted within 30 days. This action cannot be undone.', fr: 'Va dans Profil → Paramètres → Compte → Supprimer le compte. Toutes tes données seront définitivement supprimées dans les 30 jours. Cette action ne peut pas être annulée.' },
          },
        ],
      },
    ],
  },

  press: {
    icon: '✦',
    title: { en: 'Press & Media', fr: 'Presse et médias' },
    subtitle: {
      en: 'Press kit, brand assets, and media contacts.',
      fr: 'Kit presse, ressources de marque et contacts médias.',
    },
    sections: [
      {
        heading: { en: 'About BeeLove', fr: 'À propos de BeeLove' },
        body: {
          en: `BeeLove is a Montréal-based dating app that uses friend-answered compatibility quizzes to create more authentic matches. Unlike traditional dating apps where users curate their own image, BeeLove profiles are partly built by the people who know the user best.\n\nFounded in 2024, BeeLove operates in the Montréal network with plans to expand to other Canadian and French cities.`,
          fr: `BeeLove est une application de rencontres basée à Montréal qui utilise des quiz de compatibilité remplis par des amis pour créer des matchs plus authentiques. Contrairement aux applications de rencontres traditionnelles où les utilisateurs gèrent leur propre image, les profils BeeLove sont en partie construits par les personnes qui connaissent le mieux l'utilisateur.\n\nFondée en 2024, BeeLove opère dans le réseau de Montréal avec des plans d'expansion vers d'autres villes canadiennes et françaises.`,
        },
      },
      {
        heading: { en: 'Press contact', fr: 'Contact presse' },
        contacts: [
          { label: { en: 'Media enquiries', fr: 'Demandes médias' }, value: 'press@beelove.me' },
        ],
      },
    ],
  },

  careers: {
    icon: '✦',
    title: { en: 'Join the Hive', fr: 'Rejoins la Ruche' },
    subtitle: {
      en: 'We are building the future of honest dating. Come buzz with us.',
      fr: 'Nous construisons l\'avenir des rencontres honnêtes. Viens bourdonner avec nous.',
    },
    sections: [
      {
        heading: { en: 'Open positions', fr: 'Postes ouverts' },
        body: {
          en: `We are a small, mission-driven team. We value craft, care, and clarity. We move fast but thoughtfully.\n\nWe are currently looking for:\n• Senior React / React Native engineer\n• Product designer (mobile-first)\n• Growth & community manager (Montréal)\n• Backend engineer (Node.js / Firebase)\n\nDon't see your role? If you believe in what we're building, reach out anyway.`,
          fr: `Nous sommes une petite équipe animée par une mission. Nous valorisons le soin, la clarté et la qualité. Nous avançons vite mais de manière réfléchie.\n\nNous cherchons actuellement :\n• Ingénieur senior React / React Native\n• Designer produit (mobile-first)\n• Responsable croissance & communauté (Montréal)\n• Ingénieur backend (Node.js / Firebase)\n\nTu ne vois pas ton poste ? Si tu crois en ce que nous construisons, contacte-nous quand même.`,
        },
      },
      {
        heading: { en: 'Apply', fr: 'Postuler' },
        contacts: [
          { label: { en: 'Send your CV to', fr: 'Envoie ton CV à' }, value: 'careers@beelove.me' },
        ],
      },
    ],
  },
};

function SectionContent({ section, lang }) {
  const l = lang || 'en';
  if (section.steps) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'1.2rem', marginBottom:'1.2rem' }}>
        {section.steps.map(step => (
          <div key={step.n} style={{ display:'flex', gap:'1rem' }}>
            <div style={{ width:36, height:36, flexShrink:0, borderRadius:'50%', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Cormorant Garamond',serif", fontSize:'0.9rem', color:'var(--gold)', fontWeight:700 }}>{step.n}</div>
            <div>
              <div style={{ fontSize:'0.85rem', fontWeight:600, color:'var(--white)', fontFamily:'Outfit', marginBottom:'0.3rem' }}>{(step[l]||step.en).title}</div>
              <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.7, margin:0 }}>{(step[l]||step.en).body}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (section.faqs) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'1rem', marginBottom:'1.2rem' }}>
        {section.faqs.map((faq, i) => (
          <div key={i} style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'1rem 1.1rem' }}>
            <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--gold)', marginBottom:'0.4rem' }}>{(faq.q[l]||faq.q.en)}</div>
            <p style={{ fontSize:'0.8rem', color:'var(--muted)', lineHeight:1.65, margin:0 }}>{(faq.a[l]||faq.a.en)}</p>
          </div>
        ))}
      </div>
    );
  }
  if (section.contacts) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', marginBottom:'1.2rem' }}>
        {section.contacts.map((c, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.8rem', padding:'0.75rem 1rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)' }}>
            <div>
              <div style={{ fontSize:'0.68rem', color:'var(--muted)', marginBottom:'0.1rem' }}>{c.label[l]||c.label.en}</div>
              <a href={`mailto:${c.value}`} style={{ fontSize:'0.85rem', color:'var(--gold)', textDecoration:'none', fontFamily:'Outfit' }}>{c.value}</a>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (section.body) {
    const text = section.body[l] || section.body.en || '';
    return (
      <div style={{ marginBottom:'1.2rem' }}>
        {text.split('\n\n').map((para, i) => {
          // Handle **bold** markdown
          const parts = para.split(/(\*\*[^*]+\*\*)/g);
          return (
            <p key={i} style={{ fontSize:'0.88rem', color:'var(--white)', lineHeight:1.8, marginBottom:'1rem' }}>
              {parts.map((part, j) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={j} style={{ color:'var(--gold)', fontWeight:600 }}>{part.slice(2,-2)}</strong>;
                }
                return part;
              })}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
}

export default function StaticPage({ id }) {
  const tr = useTr();
  const { lang = 'en' } = useApp();
  const l = lang;

  // Check for admin overrides
  const overrides = loadTranslationOverrides('staticPages');
  const cmsContent = loadPageContent(id, l) || {};
  const pageData = BASE_PAGES[id];

  if (!pageData) {
    return (
      <div style={{ padding:'3rem 1rem', textAlign:'center' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2rem', color:'var(--gold)', marginBottom:'0.8rem', lineHeight:1 }}>✦</div>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', color:'var(--white)' }}>{tr('static.coming_soon')}</h1>
        <p style={{ color:'var(--muted)', marginTop:'0.5rem' }}>{tr('static.being_prepared')}</p>
      </div>
    );
  }

  return (
    <div style={{ padding:'2rem 1rem 4rem', minHeight:'60vh', maxWidth:640, margin:'0 auto' }}>
      {/* Back */}
      <button onClick={() => window.history.back()} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted)', display:'flex', alignItems:'center', gap:'0.4rem', fontFamily:'Outfit', fontSize:'0.82rem', marginBottom:'2rem', padding:0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        {l === 'fr' ? 'Retour' : 'Back'}
      </button>

      {/* Header */}
      <div style={{ marginBottom:'2.5rem' }}>
        <div style={{ fontSize:'2.2rem', marginBottom:'0.8rem' }}>{pageData.icon}</div>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(1.8rem,5vw,2.6rem)', fontWeight:400, lineHeight:1.1, marginBottom:'0.6rem', color:'var(--white)' }}>
          {cmsContent.title || pageData.title[l] || pageData.title.en}
        </h1>
        <p style={{ fontSize:'0.95rem', color:'var(--muted)', lineHeight:1.65 }}>
          {cmsContent.subtitle || pageData.subtitle[l] || pageData.subtitle.en}
        </p>
      </div>

      <div style={{ height:1, background:'linear-gradient(90deg, var(--gold-border), transparent)', marginBottom:'2.5rem' }}/>

      {/* Sections */}
      {pageData.sections.map((section, i) => (
        <div key={i} style={{ marginBottom:'2.5rem' }}>
          {section.heading && (
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', fontWeight:400, color:'var(--gold)', marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.6rem' }}>
              {section.heading[l] || section.heading.en}
            </h2>
          )}
          <SectionContent section={section} lang={l}/>
        </div>
      ))}

      {/* CTA */}
      {(id === 'how' || id === 'pricing') && (
        <div style={{ marginTop:'2.5rem' }}>
          <button onClick={() => { import('../utils/navigate').then(({ navigate }) => navigate('auth')); }} style={{ padding:'0.85rem 2rem', background:'var(--gold)', border:'none', color:'var(--black)', fontFamily:'Outfit', fontWeight:600, fontSize:'0.88rem', letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', borderRadius:'var(--r-sm)' }}>
            {l === 'fr' ? 'Commencer — ' : 'Get started — '}
            <em style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontWeight:400 }}>Bee Loved</em> ✦
          </button>
        </div>
      )}
    </div>
  );
}
